import { Dialog, InputAdornment } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import { Gear as DetailsIcon } from "@phosphor-icons/react/dist/ssr/Gear";
import { useFormik } from "formik";
import { MuiColorInput } from "mui-color-input";
import { FC } from "react";
import { DataTable } from "~ui-components/components/atoms/data-table";
import {
	LineQuantityConfigs,
	SymbolValue,
} from "~ui-components/types/__generated/gql/graphql";

export interface QuantityCustomLegendProps extends QuantityTableProps {
	onCancel: () => void;
}

export const QuantityCustomLegend: FC<QuantityCustomLegendProps> = ({
	form,
	onCancel,
}) => {
	return (
		<Dialog
			open
			fullScreen
			scroll="body"
			onClose={onCancel}>
			<Card
				sx={{ m: "44px" }}
				component="form"
				onSubmit={form.handleSubmit}>
				<CardHeader
					avatar={
						<Avatar>
							<DetailsIcon fontSize="var(--Icon-fontSize)" />
						</Avatar>
					}
					title="Custom class break"
				/>
				<CardContent>
					<Stack
						direction="row"
						spacing={2}>
						<FormControl>
							<InputLabel sx={{ my: 2 }}>Layout</InputLabel>
							<RadioGroup
								aria-labelledby="map-load-bounds-buttons-group-label"
								value={form.values.legendType}
								name="legendType"
								onChange={form.handleChange}>
								<FormControlLabel
									control={<Radio value="vertical" />}
									label="Vertical"
								/>
								<FormControlLabel
									control={<Radio value="horizontal" />}
									label="Horizontal"
								/>
							</RadioGroup>
						</FormControl>
					</Stack>
					<Stack
						spacing={2}
						sx={{ mt: 2 }}>
						<QuantityTable form={form} />
					</Stack>
				</CardContent>
				<CardActions sx={{ justifyContent: "flex-end" }}>
					<Button
						color="secondary"
						onClick={onCancel}>
						Cancel
					</Button>
					<Button
						variant="contained"
						type="submit">
						Save changes
					</Button>
				</CardActions>
			</Card>
		</Dialog>
	);
};

interface QuantityTableProps {
	form: ReturnType<
		typeof useFormik<Pick<LineQuantityConfigs, "legendType" | "symbolValues">>
	>;
}

const QuantityTable: FC<QuantityTableProps> = ({ form }) => {
	return (
		<DataTable<SymbolValue>
			rows={form.values.symbolValues ?? []}
			columns={[
				{
					formatter: (_, index) => {
						return (
							<MuiColorInput
								format="hex"
								sx={{ width: 200 }}
								value={
									(form.values.symbolValues ?? [])[index]?.symbol?.fill
										?.color ?? ""
								}
								onChange={(value) =>
									form.setFieldValue(
										`symbolValues[${index}].symbol.fill.color`,
										value,
									)
								}
							/>
						);
					},
					name: "Click to edit",
					width: "250px",
				},
				{
					formatter: (_, index) => (
						<OutlinedInput
							type="number"
							startAdornment={
								<InputAdornment position="start">
									{index === 0 ? ">=" : ">"}
								</InputAdornment>
							}
							{...form.getFieldProps(`symbolValues[${index}].min`)}
							onChange={(e) => {
								form.getFieldProps(`symbolValues[${index}].min`).onChange(e);
								void form.setFieldValue(
									`symbolValues[${index}].label`,
									`${index === 0 ? ">=" : ">"} ${e.target.value}`,
								);
							}}
						/>
					),
					name: "Range",
					width: "250px",
				},
				{
					formatter: (_, index) => (
						<OutlinedInput
							{...form.getFieldProps(`symbolValues[${index}].label`)}
						/>
					),
					name: "Label",
					width: "250px",
				},
			]}
		/>
	);
};
